import React, { useState, useRef, useCallback } from 'react';
import {
  Paper, Tabs, Tab, Box, Typography, IconButton, Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';

const PANEL_WIDTH = 440;
const PANEL_HEIGHT = 540;
const MIN_WIDTH = 300;
const MIN_HEIGHT = 260;

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
      {children}
    </Box>
  );
}

function ScheduleRow({ label, time, indent = false, type = 'normal' }) {
  const isMeal = type === 'meal';
  const isSession = type === 'session';
  const isBreak = type === 'break';
  const isAdjudication = type === 'adjudication';
  const isSpecial = type === 'special';

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      pl: indent ? 2.5 : 0,
      py: isMeal ? 0.6 : isSession ? 0.5 : 0.25,
      mt: (isMeal || isSession || isSpecial) ? 0.5 : 0,
    }}>
      <Typography
        variant="body2"
        sx={{
          fontSize: isMeal ? '0.85rem' : isSession ? '0.85rem' : isSpecial ? '0.82rem' : '0.8rem',
          fontWeight: isMeal ? 700 : isSession ? 700 : isBreak ? 600 : isSpecial ? 600 : 400,
          color: isMeal
            ? 'secondary.main'
            : isBreak
            ? 'warning.main'
            : isAdjudication
            ? 'text.secondary'
            : isSpecial
            ? 'primary.main'
            : 'text.primary',
          fontStyle: isAdjudication ? 'italic' : 'normal',
        }}
      >
        {label}
      </Typography>
      {time && (
        <Typography variant="body2" sx={{
          fontSize: '0.78rem',
          color: isBreak ? 'warning.main' : 'text.secondary',
          ml: 1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {time}
        </Typography>
      )}
    </Box>
  );
}

function WednesdayTab() {
  return (
    <>
      <ScheduleRow label="Welcome" time="9:00 – 9:20 am" type="special" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Session 1" time="9:20 am" type="session" />
      <ScheduleRow label="Entries 1 – 17" indent />
      <ScheduleRow label="BREAK" time="10:20 – 10:40 am" indent type="break" />
      <ScheduleRow label="Entries 18 – 30" indent />
      <ScheduleRow label="Adjudication" time="11:30 am" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="LUNCH" time="12:00 – 1:00 pm" type="meal" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Session 2" time="1:00 pm" type="session" />
      <ScheduleRow label="Entries 31 – 51" indent />
      <ScheduleRow label="BREAK" time="2:25 – 2:45 pm" indent type="break" />
      <ScheduleRow label="Entries 52 – 71" indent />
      <ScheduleRow label="Adjudication" time="4:00 pm" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="DINNER" time="4:30 – 6:00 pm" type="meal" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Session 3" time="6:00 pm" type="session" />
      <ScheduleRow label="Entries 72 – 83" indent />
      <ScheduleRow label="BREAK" time="6:50 – 7:10 pm" indent type="break" />
      <ScheduleRow label="Entries 84 – 96" indent />
      <ScheduleRow label="Adjudication" time="8:00 pm" indent type="adjudication" />
    </>
  );
}

function ThursdayTab() {
  return (
    <>
      <ScheduleRow label="Session 4" time="9:00 am" type="session" />
      <ScheduleRow label="Entries 97 – 115" indent />
      <ScheduleRow label="BREAK" time="10:15 – 10:35 am" indent type="break" />
      <ScheduleRow label="Entries 116 – 128" indent />
      <ScheduleRow label="Adjudication" time="11:30 am" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="LUNCH" time="12:00 – 1:00 pm" type="meal" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Session 5" time="1:00 pm" type="session" />
      <ScheduleRow label="Entries 129 – 150" indent />
      <ScheduleRow label="BREAK" time="2:25 – 2:45 pm" indent type="break" />
      <ScheduleRow label="Entries 151 – 170" indent />
      <ScheduleRow label="Adjudication" time="4:00 pm" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="DINNER" time="4:30 – 6:00 pm" type="meal" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Code Presentation" time="6:00 – 6:05 pm" type="special" />
      <ScheduleRow label="Session 6" time="6:05 pm" type="session" />
      <ScheduleRow label="Entries 171 – 186" indent />
      <ScheduleRow label="Adjudication" time="7:05 pm" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="FREESTYLE – Contemporary" indent />
      <ScheduleRow label="Adjudication Comments" time="7:45 pm" indent type="adjudication" />
      <ScheduleRow label="FREESTYLE – Hip-Hop" indent />
      <ScheduleRow label="Adjudication Comments" time="8:20 pm" indent type="adjudication" />
    </>
  );
}

function FridayTab() {
  return (
    <>
      <ScheduleRow label="Session 7" time="9:00 am" type="session" />
      <ScheduleRow label="Entries 187 – 208" indent />
      <ScheduleRow label="BREAK" time="10:20 – 10:40 am" indent type="break" />
      <ScheduleRow label="Entries 209 – 220" indent />
      <ScheduleRow label="Adjudication" time="11:30 am" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="LUNCH" time="12:00 – 1:00 pm" type="meal" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Session 8" time="1:00 pm" type="session" />
      <ScheduleRow label="Entries 221 – 251 (Grad Solos)" indent />
      <ScheduleRow label="Adjudication" time="2:35 pm" indent type="adjudication" />
      <Divider sx={{ my: 0.75 }} />

      <ScheduleRow label="Performance" time="3:00 – 3:15 pm" type="special" />
      <ScheduleRow label="Discussion" time="3:15 – 3:50 pm" type="special" />
      <ScheduleRow label="Overall Awards of Excellence" time="3:30 – 4:45 pm" type="special" />
    </>
  );
}

function SchedulePanel({ open, onClose }) {
  const [tab, setTab] = useState(0);
  const [pos, setPos] = useState({ x: Math.max(0, window.innerWidth - PANEL_WIDTH - 40), y: 80 });
  const [size, setSize] = useState({ w: PANEL_WIDTH, h: PANEL_HEIGHT });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const resizing = useRef(false);
  const resizeStart = useRef({ mouseX: 0, mouseY: 0, w: 0, h: 0 });

  const onDragMouseDown = useCallback((e) => {
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };

    const onMouseMove = (e) => {
      if (!dragging.current) return;
      const x = Math.min(Math.max(0, e.clientX - dragOffset.current.x), window.innerWidth - size.w);
      const y = Math.min(Math.max(0, e.clientY - dragOffset.current.y), window.innerHeight - 60);
      setPos({ x, y });
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [pos, size]);

  const onResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { mouseX: e.clientX, mouseY: e.clientY, w: size.w, h: size.h };

    const onMouseMove = (e) => {
      if (!resizing.current) return;
      const w = Math.max(MIN_WIDTH, resizeStart.current.w + (e.clientX - resizeStart.current.mouseX));
      const h = Math.max(MIN_HEIGHT, resizeStart.current.h + (e.clientY - resizeStart.current.mouseY));
      setSize({ w, h });
    };
    const onMouseUp = () => {
      resizing.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [size]);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        zIndex: 1400,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Title bar / drag handle */}
      <Box
        onMouseDown={onDragMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ mr: 1, opacity: 0.8, fontSize: '1.1rem' }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, fontSize: '0.9rem' }}>
          Schedule
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          onMouseDown={e => e.stopPropagation()}
          sx={{ color: 'primary.contrastText', p: 0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
        TabIndicatorProps={{ style: { height: 3 } }}
      >
        <Tab label="Wed May 27" sx={{ fontSize: '0.72rem', minHeight: 36, py: 0.5 }} />
        <Tab label="Thu May 28" sx={{ fontSize: '0.72rem', minHeight: 36, py: 0.5 }} />
        <Tab label="Fri May 29" sx={{ fontSize: '0.72rem', minHeight: 36, py: 0.5 }} />
      </Tabs>

      {/* Tab content — scrollable */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <TabPanel value={tab} index={0}><WednesdayTab /></TabPanel>
        <TabPanel value={tab} index={1}><ThursdayTab /></TabPanel>
        <TabPanel value={tab} index={2}><FridayTab /></TabPanel>
      </Box>

      {/* Resize handle — bottom-right corner */}
      <Box
        onMouseDown={onResizeMouseDown}
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 18,
          height: 18,
          cursor: 'nwse-resize',
          zIndex: 1,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 3,
            right: 3,
            width: 10,
            height: 10,
            borderRight: '2px solid',
            borderBottom: '2px solid',
            borderColor: 'text.disabled',
            borderRadius: '0 0 2px 0',
          },
        }}
      />
    </Paper>
  );
}

export default SchedulePanel;
